import { yupResolver } from '@hookform/resolvers/yup'
import { findIndex, isEqual, map } from 'lodash'
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useParams } from 'react-router-dom'
import * as yup from 'yup'

import { SeoPages, SeoSettings } from '../../../common/models'
import { useCheckPermission } from '../../../core/hooks/use-check-permission'
import {
  getSeoSettings,
  updateSeoSettings,
} from '../../../core/services/festival-settings'
import { uploadImage } from '../../../core/services/upload-image'
import { MessageBox } from '../messages/MessageBox'
import { Header } from '@/components'
import { Card } from '@/components/ui'
import { SeoPageForm } from './SeoPageForm'
import {
  ISEOValues,
  SeoImageFieldName,
  SEO_ALL_FIELDS,
  SEO_PAGE_LABELS,
  toSeoFormItem,
  toSeoImageStates,
  toSeoPayload,
} from './seo.config'

interface SeoImageState {
  name: string
  url: string
}

export const SEOPage: React.FC = () => {
  const { editionId, festivalName } = useParams()

  const [seo, setSeo] = useState<SeoSettings[]>([])
  const [isUploading, setIsUploading] = useState(false)
  const [isErrorRatio, setIsErrorRatio] = useState<boolean>(false)
  const [isError, setIsError] = useState<boolean>(false)
  const [errorStatus, setErrorStatus] = useState<number | undefined>(undefined)
  const [showMessageBox, setShowMessageBox] = useState<boolean>(false)
  const [message, setMessage] = useState<string>('')
  const [invalidPhoto, setInvalidPhoto] = useState<Record<string, boolean>>({})
  const [images, setImages] = useState<Record<string, SeoImageState>>({})

  const seos: SeoPages[] = ['HOMEPAGE', 'TICKETS']

  const { hasEditPermission } = useCheckPermission({ page: 'SETTINGS' })

  const schema = yup.object().shape({
    content: yup
      .array()
      .of(
        yup.object().shape({
          googleTagManagerId: yup.string().nullable().optional(),
          title: yup.string().nullable().optional(),
          metaTitle: yup.string().nullable().optional(),
          metaDescription: yup.string().nullable().optional(),
          metaKeywords: yup.string().nullable().optional(),
          metaOgImageKey: yup.string().nullable().optional(),
          metaOgTitle: yup.string().nullable().optional(),
          metaOgDescription: yup.string().nullable().optional(),
          metaOgSiteName: yup.string().nullable().optional(),
          metaOgType: yup.string().nullable().optional(),
          metaOgUrl: yup.string().nullable().optional(),
          metaOgImageSecureKey: yup.string().nullable().optional(),
          metaOgImageWidth: yup
            .number()
            .transform((v) => (Number.isNaN(v) ? undefined : v))
            .nullable()
            .optional(),
          metaOgImageHeight: yup
            .number()
            .transform((v) => (Number.isNaN(v) ? undefined : v))
            .nullable()
            .optional(),
          metaOgImageType: yup.string().nullable().optional(),
          metaOgImageAlt: yup.string().nullable().optional(),
          metaOgLocale: yup.string().nullable().optional(),
          metaTwitterCard: yup.string().nullable().optional(),
          metaTwitterUrl: yup.string().nullable().optional(),
          metaTwitterTitle: yup.string().nullable().optional(),
          metaTwitterDescription: yup.string().nullable().optional(),
          metaTwitterImageKey: yup.string().nullable().optional(),
        }),
      )
      .required(),
  })

  const {
    handleSubmit,
    register,
    formState: { errors, isSubmitting },
    setValue,
  } = useForm<ISEOValues>({
    mode: 'onChange',
    resolver: yupResolver<ISEOValues>(schema),
  })

  const hydrateForm = (index: number, data: SeoSettings) => {
    const formItem = toSeoFormItem(data)
    SEO_ALL_FIELDS.forEach((field) => {
      setValue(`content.${index}.${field}`, formItem[field])
    })
    setImages((prev) => ({ ...prev, ...toSeoImageStates(index, data) }))
  }

  useEffect(() => {
    // prettier-ignore
    (async () => {
      try {
        const responses = await Promise.all(map(seos, (s) => getSeoSettings(s)))
        const array: SeoSettings[] = []
        responses.forEach(({ data }, index) => {
          array[index] = data
          hydrateForm(index, data)
        })
        setSeo(array)
      } catch (e) {
        if (e instanceof Error) {
          setIsError(true)
          setShowMessageBox(true)
          setMessage(e.message)
          setErrorStatus((e as { response?: { status?: number } })?.response?.status)
        } else {
          console.error(e)
        }
      }
    })()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editionId, festivalName])

  const submit = async (values: ISEOValues, page: SeoPages) => {
    const index = findIndex(seos, (s) => isEqual(s, page))
    const data = toSeoPayload(values.content[index])

    try {
      const { status } = await updateSeoSettings(page, data)
      if (status === 200) {
        setIsError(false)
        setShowMessageBox(true)
        setMessage('Update Successful')
      }
    } catch (e) {
      if (e instanceof Error) {
        setIsError(true)
        setShowMessageBox(true)
        setMessage(e.message)
        setErrorStatus(
          (e as { response?: { status?: number } })?.response?.status,
        )
      } else {
        console.error(e)
      }
    }
  }

  const handleImageChange = async (
    field: SeoImageFieldName,
    index: number,
    file: File,
  ) => {
    const key = `${index}.${field}`
    if (file.size > 1_000_000) {
      setInvalidPhoto((prev) => ({ ...prev, [key]: true }))
      setIsError(true)
      setShowMessageBox(true)
      setMessage('The photo is too large (1 MB)')
      return
    }

    const { photoUrl, photoKey } = await uploadImage(
      file,
      setIsUploading,
      setIsError,
      setShowMessageBox,
      setMessage,
      setIsErrorRatio,
      'SEO_SETTINGS_DEFAULT_META',
      false,
    )

    if (photoUrl && photoKey) {
      setInvalidPhoto((prev) => ({ ...prev, [key]: false }))
      setValue(`content.${index}.${field}`, photoKey, {
        shouldValidate: true,
        shouldDirty: true,
      })
      setImages((prev) => ({
        ...prev,
        [key]: { name: photoKey, url: photoUrl },
      }))
    } else {
      setInvalidPhoto((prev) => ({ ...prev, [key]: true }))
    }
  }

  const handleImageRemove = (field: SeoImageFieldName, index: number) => {
    const key = `${index}.${field}`
    setImages((prev) => ({ ...prev, [key]: { name: '', url: '' } }))
    setValue(`content.${index}.${field}`, '', { shouldDirty: true })
  }

  const handleCancel = (index: number) => {
    if (seo[index]) hydrateForm(index, seo[index])
  }

  return (
    <>
      <Header pageTitle="Settings" pageSubtitle="SEO Settings" />
      <div className="max-h-[calc(100vh-118px)] overflow-y-auto space-y-4">
        {map(seos, (s, index) => {
          return (
            <Card
              className="flex flex-col gap-2 p-7.5 bg-gradient-4 border border-monochrome-120"
              key={s}
            >
              <span className="text-title-xs text-monochrome-1 mb-6">
                {SEO_PAGE_LABELS[s]}
              </span>
              <SeoPageForm
                index={index}
                page={s}
                register={register}
                errors={errors}
                hasEditPermission={hasEditPermission}
                isUploading={isUploading}
                isErrorRatio={isErrorRatio}
                images={images}
                invalidPhoto={invalidPhoto}
                onImageChange={handleImageChange}
                onImageRemove={handleImageRemove}
                onCancel={handleCancel}
                onSubmit={handleSubmit((values) => submit(values, s))}
                isSubmitting={isSubmitting}
              />
            </Card>
          )
        })}
      </div>
      <MessageBox
        message={message}
        setShowMessageBox={setShowMessageBox}
        showMessageBox={showMessageBox}
        isError={isError}
        errorStatus={errorStatus}
      />
    </>
  )
}
